/**
 * WorkflowTestingPanel - Testing framework UI
 * ZAPHUB 2.0 - Create tests, run tests, view coverage
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  TestTube,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Trash2,
  Download,
  BarChart3,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import type { WorkflowTest, TestRun, Workflow, TestAssertion } from '@shared/types';

interface WorkflowTestingPanelProps {
  workflow: Workflow;
  onRunTests?: (testIds: string[]) => void;
}

export const WorkflowTestingPanel: React.FC<WorkflowTestingPanelProps> = ({
  workflow,
  onRunTests,
}) => {
  const [tests, setTests] = useState<WorkflowTest[]>([]);
  const [testRuns, setTestRuns] = useState<Map<string, TestRun[]>>(new Map());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState<WorkflowTest | null>(null);
  const [coverage, setCoverage] = useState({
    totalNodes: 10,
    testedNodes: 7,
    coverage: 70,
    untestedNodes: ['node-4', 'node-8', 'node-9'],
  });

  // New test form
  const [testName, setTestName] = useState('');
  const [testDescription, setTestDescription] = useState('');
  const [mockData, setMockData] = useState('{}');
  const [assertions, setAssertions] = useState<
    Array<{ nodeId: string; condition: string; expected: string }>
  >([]);

  useEffect(() => {
    fetchTests();
  }, [workflow.id]);

  const fetchTests = async () => {
    // Mock data - in production, call API
    const mockTests: WorkflowTest[] = [
      {
        id: 'test1',
        workflowId: workflow.id,
        name: 'Happy Path Test',
        description: 'Tests the main success scenario',
        mockData: { input: 'test@example.com' },
        assertions: [
          {
            nodeId: 'node-1',
            condition: 'exists',
            expected: true,
            actual: true,
            passed: true,
          },
          {
            nodeId: 'node-2',
            condition: 'equals',
            expected: 'Email sent successfully',
            actual: 'Email sent successfully',
            passed: true,
          },
        ],
        status: 'passing',
        lastRun: new Date(Date.now() - 2 * 60 * 60 * 1000),
        createdBy: 'john.doe',
        createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
      },
      {
        id: 'test2',
        workflowId: workflow.id,
        name: 'Error Handling Test',
        description: 'Tests workflow behavior on invalid input',
        mockData: { input: null },
        assertions: [
          {
            nodeId: 'node-1',
            condition: 'exists',
            expected: false,
            actual: null,
            passed: false,
          },
        ],
        status: 'failing',
        lastRun: new Date(Date.now() - 1 * 60 * 60 * 1000),
        createdBy: 'john.doe',
        createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000),
      },
      {
        id: 'test3',
        workflowId: workflow.id,
        name: 'Performance Test',
        description: 'Tests execution time is under 5 seconds',
        mockData: { input: 'performance@test.com', load: 100 },
        assertions: [
          {
            nodeId: 'node-execution',
            condition: 'less_than',
            expected: 5000,
            actual: 3200,
            passed: true,
          },
        ],
        status: 'passing',
        lastRun: new Date(Date.now() - 30 * 60 * 1000),
        createdBy: 'jane.smith',
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 30 * 60 * 1000),
      },
    ];

    setTests(mockTests);

    // Mock test runs
    const runs = new Map<string, TestRun[]>();
    mockTests.forEach((test) => {
      runs.set(test.id, [
        {
          id: 'run1',
          testId: test.id,
          workflowId: workflow.id,
          status: test.status === 'passing' ? 'passed' : 'failed',
          assertions: test.assertions,
          duration: 1234,
          ranAt: test.lastRun!,
        },
      ]);
    });
    setTestRuns(runs);
  };

  const handleCreateTest = async () => {
    if (!testName.trim()) {
      alert('Please enter a test name');
      return;
    }

    try {
      const parsedMockData = JSON.parse(mockData);
      
      const newTest: WorkflowTest = {
        id: `test-${Date.now()}`,
        workflowId: workflow.id,
        name: testName,
        description: testDescription,
        mockData: parsedMockData,
        assertions: assertions.map((a) => ({
          ...a,
          condition: a.condition as TestAssertion['condition'],
          expected: a.expected,
        })),
        status: 'passing',
        createdBy: 'current-user',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setTests([...tests, newTest]);
      setShowCreateDialog(false);
      resetForm();
    } catch (err) {
      alert('Invalid JSON in mock data');
    }
  };

  const handleRunTest = async (testId: string) => {
    // Mock - in production, call API
    console.log('Running test:', testId);
    if (onRunTests) {
      onRunTests([testId]);
    }
  };

  const handleRunAllTests = async () => {
    // Mock - in production, call API
    console.log('Running all tests');
    if (onRunTests) {
      onRunTests(tests.map((t) => t.id));
    }
  };

  const handleGenerateTests = async () => {
    // Mock - in production, call AI API to generate tests
    console.log('Generating tests with AI');
    alert('AI will analyze your workflow and generate comprehensive test cases');
  };

  const handleDeleteTest = async (testId: string) => {
    if (!confirm('Are you sure you want to delete this test?')) {
      return;
    }
    setTests(tests.filter((t) => t.id !== testId));
  };

  const resetForm = () => {
    setTestName('');
    setTestDescription('');
    setMockData('{}');
    setAssertions([]);
  };

  const addAssertion = () => {
    setAssertions([
      ...assertions,
      { nodeId: '', condition: 'equals', expected: '' },
    ]);
  };

  const removeAssertion = (index: number) => {
    setAssertions(assertions.filter((_, i) => i !== index));
  };

  const updateAssertion = (index: number, field: string, value: string) => {
    const updated = [...assertions];
    updated[index] = { ...updated[index], [field]: value };
    setAssertions(updated);
  };

  const passingTests = tests.filter((t) => t.status === 'passing').length;
  const failingTests = tests.filter((t) => t.status === 'failing').length;
  const totalTests = tests.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl mb-1">Workflow Testing</h2>
          <p className="text-gray-600">Test {workflow.name} before deployment</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleGenerateTests}>
            <Sparkles className="h-4 w-4 mr-2" />
            AI Generate Tests
          </Button>
          <Button variant="outline" onClick={handleRunAllTests}>
            <Play className="h-4 w-4 mr-2" />
            Run All Tests
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Test
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <TestTube className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl">{totalTests}</div>
              <div className="text-sm text-gray-600">Total Tests</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl text-green-600">{passingTests}</div>
              <div className="text-sm text-gray-600">Passing</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl text-red-600">{failingTests}</div>
              <div className="text-sm text-gray-600">Failing</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl">{coverage.coverage}%</div>
              <div className="text-sm text-gray-600">Coverage</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="tests" className="w-full">
        <TabsList>
          <TabsTrigger value="tests">Tests</TabsTrigger>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="history">Run History</TabsTrigger>
        </TabsList>

        <TabsContent value="tests" className="mt-6 space-y-4">
          {tests.map((test) => {
            const runs = testRuns.get(test.id) || [];
            const lastRun = runs[0];

            return (
              <Card key={test.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg">{test.name}</h3>
                      <Badge
                        variant={test.status === 'passing' ? 'default' : 'destructive'}
                        className={test.status === 'passing' ? 'bg-green-500' : ''}
                      >
                        {test.status === 'passing' ? (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1" />
                        )}
                        {test.status}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{test.description}</p>

                    {/* Assertions */}
                    <div className="space-y-2 mb-4">
                      <div className="text-sm text-gray-600">
                        Assertions: {test.assertions.length}
                      </div>
                      {test.assertions.map((assertion, index) => (
                        <div
                          key={index}
                          className={`text-sm p-2 rounded ${
                            assertion.passed
                              ? 'bg-green-50 text-green-800'
                              : 'bg-red-50 text-red-800'
                          }`}
                        >
                          {assertion.passed ? (
                            <CheckCircle className="h-4 w-4 inline mr-2" />
                          ) : (
                            <XCircle className="h-4 w-4 inline mr-2" />
                          )}
                          Node {assertion.nodeId}: {assertion.condition} {' '}
                          {JSON.stringify(assertion.expected)}
                        </div>
                      ))}
                    </div>

                    {/* Last Run */}
                    {lastRun && (
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>Last run: {test.lastRun?.toLocaleString()}</span>
                        </div>
                        <div>Duration: {lastRun.duration}ms</div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRunTest(test.id)}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Run
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTest(test.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}

          {tests.length === 0 && (
            <Card className="p-12 text-center">
              <TestTube className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl mb-2">No tests yet</h3>
              <p className="text-gray-600 mb-6">
                Create your first test or let AI generate tests for you
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={handleGenerateTests}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI Generate
                </Button>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Test
                </Button>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="coverage" className="mt-6">
          <Card className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg">Test Coverage</h3>
                <span className="text-3xl text-purple-600">{coverage.coverage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-purple-600 h-4 rounded-full transition-all"
                  style={{ width: `${coverage.coverage}%` }}
                />
              </div>
              <div className="flex justify-between text-sm text-gray-600 mt-2">
                <span>{coverage.testedNodes} of {coverage.totalNodes} nodes tested</span>
                <span>{coverage.untestedNodes.length} untested</span>
              </div>
            </div>

            {coverage.untestedNodes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  <h4 className="text-orange-600">Untested Nodes</h4>
                </div>
                <div className="space-y-2">
                  {coverage.untestedNodes.map((nodeId) => (
                    <div key={nodeId} className="bg-orange-50 border-l-4 border-orange-500 p-3">
                      <span className="text-orange-900">Node: {nodeId}</span>
                    </div>
                  ))}
                </div>
                <Button className="mt-4" variant="outline" onClick={handleGenerateTests}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Tests for Untested Nodes
                </Button>
              </div>
            )}

            {coverage.coverage === 100 && (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl text-green-600 mb-2">100% Coverage!</h3>
                <p className="text-gray-600">All nodes are covered by tests</p>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg mb-4">Test Run History</h3>
            <div className="space-y-3">
              {Array.from(testRuns.entries()).map(([testId, runs]) => {
                const test = tests.find((t) => t.id === testId);
                if (!test) return null;

                return runs.map((run) => (
                  <div
                    key={run.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {run.status === 'passed' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <div>
                        <div className="mb-1">{test.name}</div>
                        <div className="text-sm text-gray-600">
                          {run.ranAt.toLocaleString()} • {run.duration}ms
                        </div>
                      </div>
                    </div>
                    <Badge variant={run.status === 'passed' ? 'default' : 'destructive'}>
                      {run.status}
                    </Badge>
                  </div>
                ));
              })}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Test Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Test</DialogTitle>
            <DialogDescription>
              Define test cases to validate your workflow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[600px] overflow-y-auto">
            <div>
              <Label>Test Name</Label>
              <Input
                placeholder="e.g., Happy Path Test"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe what this test validates..."
                value={testDescription}
                onChange={(e) => setTestDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div>
              <Label>Mock Data (JSON)</Label>
              <Textarea
                placeholder='{"input": "test@example.com"}'
                value={mockData}
                onChange={(e) => setMockData(e.target.value)}
                rows={4}
                className="font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Assertions</Label>
                <Button variant="outline" size="sm" onClick={addAssertion}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Assertion
                </Button>
              </div>

              {assertions.map((assertion, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                  <Input
                    placeholder="Node ID"
                    value={assertion.nodeId}
                    onChange={(e) => updateAssertion(index, 'nodeId', e.target.value)}
                    className="col-span-3"
                  />
                  <Select
                    value={assertion.condition}
                    onValueChange={(v) => updateAssertion(index, 'condition', v)}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Equals</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="exists">Exists</SelectItem>
                      <SelectItem value="not_exists">Not Exists</SelectItem>
                      <SelectItem value="greater_than">Greater Than</SelectItem>
                      <SelectItem value="less_than">Less Than</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Expected value"
                    value={assertion.expected}
                    onChange={(e) => updateAssertion(index, 'expected', e.target.value)}
                    className="col-span-5"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAssertion(index)}
                    className="col-span-1"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}

              {assertions.length === 0 && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  No assertions yet. Click "Add Assertion" to create one.
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTest}>
              <TestTube className="h-4 w-4 mr-2" />
              Create Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
